import { apiClient, endpoints } from '../api/client';

export const AUTO_PROCESS_LOCK_HEARTBEAT_MS = 25000;

export type AutoProcessLockStatus = {
  acquired?: boolean;
  held: boolean;
  owner?: string;
  brand?: string;
  message?: string;
  skipped?: boolean;
  started_at?: string;
};

function lockErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function newAutoProcessLockOwner(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `lock-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export async function acquireAutoProcessLock(
  brand: string,
  owner: string,
  stage?: string
): Promise<AutoProcessLockStatus> {
  try {
    const res = await apiClient.post(endpoints.reviewsAutoProcessLock(brand, owner, stage));
    return res.data || { acquired: true, held: false, owner, brand };
  } catch (err) {
    return {
      acquired: false,
      held: true,
      brand,
      message: lockErrorMessage(
        err,
        `Auto-process is already running for ${brand}. Stop it in the other tab or browser.`
      ),
    };
  }
}

export async function heartbeatAutoProcessLock(brand: string, owner: string): Promise<boolean> {
  try {
    await apiClient.post(endpoints.reviewsAutoProcessLockHeartbeat(brand, owner));
    return true;
  } catch {
    return false;
  }
}

export async function releaseAutoProcessLock(brand: string, owner: string): Promise<void> {
  try {
    await apiClient.post(endpoints.reviewsAutoProcessLockRelease(brand, owner));
  } catch (err) {
    console.warn('Failed to release Auto-process lock:', err);
  }
}

export function releaseAutoProcessLockBeacon(brand: string, owner: string): void {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
  const path = endpoints.reviewsAutoProcessLockRelease(brand, owner);
  const url =
    typeof window !== 'undefined' && path.startsWith('/')
      ? `${window.location.origin}${path}`
      : path;
  try {
    navigator.sendBeacon(url);
  } catch {
    // Best-effort on tab close.
  }
}

export async function getAutoProcessLockStatus(brand: string): Promise<AutoProcessLockStatus> {
  try {
    const res = await apiClient.get(endpoints.reviewsAutoProcessLock(brand));
    const data = res.data || {};
    return {
      held: Boolean(data.held),
      owner: data.owner,
      brand: data.brand || brand,
      message: data.message,
      skipped: Boolean(data.skipped),
    };
  } catch {
    return { held: false, brand };
  }
}
